import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Divider, Dropdown, Row, Tooltip } from 'antd';
import styled from 'styled-components';
import i18n from '../../../../../../i18n/i18n';
import { AutoTooltip } from '../../../components/common/AutoTooltip';
import { ReactComponent as IconMetaExplorer } from '../../../../../../images/icon-meta-explorer.svg';
import { ReactComponent as IconClose } from '../../../../../../images/icon-close.svg';
import { SingleAssetAuditResult } from '../../../../../../types.generated';
import ActivityTagColumn, { ActivityStatus } from './ActivityTagColumn';
import TimeColumn from './TimeColumn';
import { useGetUserQuery } from '../../../../../../graphql/user.generated';
import { DATAHUB_SYSTEM } from '../../../../../search/utils/constants';

const ReadMoreButton = styled.button`
    background: none;
    border: none;
    color: ${(props) => props.theme.styles['blue-s100']};
    cursor: pointer;
    padding: 0;
    margin-left: 4px;
    font-size: inherit;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }

    &:focus {
        outline: none;
    }
`;

interface ExpandableTextProps {
    text: string | null | undefined;
    limit?: number;
    className?: string;
}

/* eslint-disable react/prop-types */
const ExpandableText: React.FC<ExpandableTextProps> = ({ text, limit = 100, className }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!text) return <span className={className}>-</span>;

    const shouldTruncate = text.length > limit;

    if (!shouldTruncate) {
        return <span className={className}>{text}</span>;
    }

    return (
        <span className={className}>
            {isExpanded ? text : `${text.substring(0, limit)}...`}
            <ReadMoreButton onClick={() => setIsExpanded(!isExpanded)}>
                {isExpanded ? 'Show less' : 'Read more'}
            </ReadMoreButton>
        </span>
    );
};
/* eslint-enable react/prop-types */

const MetaDataExplorerDropdown = ({ record }: { record: SingleAssetAuditResult }) => {
    const { t } = useTranslation();
    const [isMetadataOpen, setIsMetadataOpen] = useState(false);

    const { data } = useGetUserQuery({ variables: { urn: record.user as string, groupsCount: 0 } });

    const fullName = data?.corpUser?.info?.fullName ?? t('System');
    const email = data?.corpUser?.username || '';

    const isSystemUser = email === DATAHUB_SYSTEM;

    const tooltipUserContent = (
        <div>
            <div>{fullName}</div>
            {!isSystemUser && <div>{email}</div>}
        </div>
    );

    const handleClose = () => {
        setIsMetadataOpen(false);
    };

    return (
        <Dropdown
            trigger={['click']}
            placement="bottomRight"
            open={isMetadataOpen}
            onOpenChange={setIsMetadataOpen}
            overlay={
                <Card className="f-metadata-explorer-card">
                    <Row wrap={false} gutter={8} align="middle">
                        <Col flex="auto">
                            <AutoTooltip className="f-metadata-title">{t('Change Log')}</AutoTooltip>
                            <Tooltip className="f-metadata-subtitle" title={tooltipUserContent}>
                                {fullName}
                            </Tooltip>
                        </Col>
                        <Col flex="none">
                            <ActivityTagColumn activity={record.activity as ActivityStatus} />
                        </Col>
                        <Col flex="none">
                            <Button
                                type="text"
                                size="small"
                                icon={<IconClose />}
                                className="f-btn-action ma-0"
                                title={i18n.t('Close Metadata Explorer')}
                                aria-label={i18n.t('Close Metadata Explorer')}
                                onClick={handleClose}
                            />
                        </Col>
                    </Row>
                    <div className="f-text-medium-content f-color-dark-black-s70 my-2">
                        <ExpandableText
                            className="f-text-medium-content f-color-dark-black-s70 f-white-space-pre-line f-word-break-break-word my-2"
                            text={record.message}
                            limit={150}
                        />
                    </div>
                    <Divider className="my-2" />

                    <div className="f-metadata-subheader mt-3 mb-2">{i18n.t('Details')}</div>

                    <Row gutter={4} align="top">
                        <Col span={10}>
                            <div className="f-metadata-label">{t('Old Value')}</div>
                        </Col>
                        <Col span={14}>
                            <div className="f-metadata-value">
                                <ExpandableText
                                    className="f-text-medium-content f-color-dark-black-s70 f-white-space-pre-line f-word-break-break-word my-2"
                                    text={record.oldValue}
                                    limit={100}
                                />
                            </div>
                        </Col>
                    </Row>

                    <Row gutter={4} align="top">
                        <Col span={10}>
                            <div className="f-metadata-label">{t('New Value')}</div>
                        </Col>
                        <Col span={14}>
                            <div className="f-metadata-value">
                                <ExpandableText
                                    className="f-text-medium-content f-color-dark-black-s70 f-white-space-pre-line f-word-break-break-word my-2"
                                    text={record.newValue}
                                    limit={100}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row gutter={4} align="top">
                        <Col span={10}>
                            <div className="f-metadata-label">{t('Attribute')}</div>
                        </Col>
                        <Col span={14}>
                            <div className="f-metadata-value">{record.attribute || '-'}</div>
                        </Col>
                    </Row>
                    <Row gutter={4} align="top">
                        <Col span={10}>
                            <div className="f-metadata-label">{t('Time')}</div>
                        </Col>
                        <Col span={14}>
                            <div className="f-metadata-value">
                                <TimeColumn value={record.time} />
                            </div>
                        </Col>
                    </Row>
                </Card>
            }
        >
            <Button
                type="text"
                icon={<IconMetaExplorer />}
                className="icon-button ma-0"
                title={i18n.t('Show Metadata Explorer')}
                aria-label={i18n.t('Show Metadata Explorer')}
            />
        </Dropdown>
    );
};

export default MetaDataExplorerDropdown;
